// ============================================================================
//  RADROOT — PUBLIC SITE CONFIGURATION  ·  single source of truth
// ============================================================================
//  This is the ONLY file you need to edit to update the public website.
//
//  · Everything under `publicInfo` is optional provider metadata.
//  · Any field left as null, "" or [] is HIDDEN on the public site — nothing
//    is invented or placeholder-filled. Sections whose fields are all empty
//    are not rendered at all.
//  · Fill these in at mainnet launch; no other code changes are required.
//
//  Page <title>, meta description and social-share tags live in
//  frontend/public/index.html (static meta tags).
// ============================================================================

export const radrootConfig = {
  site: {
    name: "Radroot",
    // Final domain — used for the canonical link and og:image absolute URL in index.html.
    url: "https://radroot.org",
  },

  status: {
    // Hero status badge — plain, factual wording (update to reflect your live status).
    badge: "Live on Flare mainnet",
  },

  // Delegation address shown in the site footer (Flare mainnet).
  delegationAddress: "0xFC936989b5BCA0bdea6B0f4A184044eb720D86A5",

  // --------------------------------------------------------------------------
  //  PUBLIC INFORMATION — Flare provider metadata
  // --------------------------------------------------------------------------
  publicInfo: {
    providerAddress: "0x648F236cc13270a69b3985C1552e9f43A8488C14",
    explorerLink: "https://coston2-systems-explorer.flare.rocks/providers/fsp/0x648F236cc13270a69b3985C1552e9f43A8488C14",
    validatorNodeIds: [], // e.g. ["NodeID-…", "NodeID-…"]
    providerFee: null,
    contactEmail: null, // e.g. "hello@radroot.xyz"
    socialLinks: [], // e.g. [{ label: "X", url: "https://x.com/…" }]
  },

  // --- SAVED FOR MAINNET LAUNCH (removed from public site 24 Sep 2026) -----
  // Coston2 explorer replica of the FSP provider page. To bring the
  // "Radroot FTSO Provider Information" panel back, rename this key to
  // `providerSnapshot` (and update the values to mainnet at launch).
  providerSnapshotSavedForLaunch: {
    snapshotDate: "24 Sep 2026",
    entityName: "0x648F...8C14",
    entityAddress: "0x648F236cc13270a69b3985C1552e9f43A8488C14",
    entityAddressShort: "0x648F2...88C14",
    fsp: {
      addresses: [
        { label: "Public key", tag: "PK", value: "0x2840...c6d6ba" },
        { label: "Identity address", tag: "IA", value: "0x648F...488C14", full: "0x648F236cc13270a69b3985C1552e9f43A8488C14", balance: "839.35" },
        { label: "Delegation address", tag: "DA", value: "0xf962...5ae929", full: "0xf96272f6f0A162f3012593cA6081A22bc65ae929", balance: "199.38" },
        { label: "Submit address", tag: "SA", value: "0xCA17...90e355", full: "0xCA17B261730dE8030E5113EafB9e8d58A590e355", balance: "296.3" },
        { label: "Submit signature address", tag: "SSA", value: "0x51FF...3cb09D", full: "0x51FF1900ac39B4d4DD782f1d02fFCb84643cb09D", balance: "197.52" },
        { label: "Signing policy address", tag: "SPA", value: "0x01E3...1d895f", full: "0x01E3526Cb537344656EA1c8B48119e5Ec11d895f", balance: "195.45" },
      ],
      fastUpdates: [
        { address: "0x50d6...152D", full: "0x50d6f845058D9e6F6EE4363A3EB052eaa47D152D", value: "299.99", timestamp: "24 Sep 2026 12:06:51" },
        { address: "0xA1a0...6D32", full: "0xA1a03FAb3484641619efcCf794fcC89629346D32", value: "299.99", timestamp: "24 Sep 2026 10:56:22" },
        { address: "0xFf86...4c16", full: "0xFf8633AC77C27067Cd6224E8AAeB5B3c3D414c16", value: "299.99", timestamp: "24 Sep 2026 13:58:30" },
      ],
      nodes: ["NodeID-MBpy...uop2"],
      reRegistration: [
        { epoch: "RE#6104", status: "Registered" },
        { epoch: "RE#6103", status: "Registered" },
        { epoch: "RE#6102", status: "Registered" },
        { epoch: "RE#6101", status: "Registered" },
      ],
      uptimeVoteSigned: [
        { epoch: "RE#6102", value: "0x290de...3e563" },
        { epoch: "RE#6101", value: "0x290de...3e563" },
        { epoch: "RE#6100", value: "0x290de...3e563" },
      ],
      rewardsSigned: [],
    },
    ftso: {
      availability: [
        { label: "Last 6 hours", value: "99.03 %" },
        { label: "Last 24 hours", value: "97.86 %" },
      ],
      successRate: [
        { label: "Last 6 hours", primary: "5.27 %", secondary: "87.48 %" },
        { label: "Last 24 hours", primary: "4.47 %", secondary: "87.38 %" },
      ],
      epochs: [
        { epoch: "RE#6103", availability: "99.52 %", primary: "5.54 %", secondary: "87.67 %" },
        { epoch: "RE#6102", availability: "98.50 %", primary: "4.71 %", secondary: "88.19 %" },
        { epoch: "RE#6101", availability: "98.52 %", primary: "4.68 %", secondary: "88.61 %" },
        { epoch: "RE#6100", availability: "95.17 %", primary: "3.06 %", secondary: "85.34 %" },
      ],
    },
  },
};

// ---------------------------------------------------------------------------
//  Helpers — turn the config into a render list. Empty fields never appear.
//  contactEmail / socialLinks render as generic rows; the provider address,
//  explorer link, fee and snapshot render in the dedicated provider panel.
// ---------------------------------------------------------------------------
export function publicInfoItems() {
  const i = radrootConfig.publicInfo;
  const items = [];
  if (i.contactEmail)
    items.push({ key: "contactEmail", label: "Contact email", kind: "email", value: i.contactEmail });
  if (i.socialLinks && i.socialLinks.length)
    items.push({ key: "socialLinks", label: "Social links", kind: "links", value: i.socialLinks });
  return items;
}

export const hasPublicInfo = () =>
  Boolean(radrootConfig.providerSnapshot) || publicInfoItems().length > 0;
